// AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  PlusCircle, 
  Search, 
  Filter, 
  Trash2, 
  Edit, 
  Eye,
  ChevronDown,
  Upload,
  Book,
  BookOpen,
  ListOrdered,
  Plus,
  Building
} from 'lucide-react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  orderBy,
  writeBatch 
} from 'firebase/firestore';
import { getAuth } from "firebase/auth";
// Firebase Storage imports
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const QuestionUpload = () => {
  // Board states
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [boards, setBoards] = useState([]);
  const [showCreateBoardModal, setShowCreateBoardModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Main states
  const [activeView, setActiveView] = useState('hierarchy');
  const [selectedStd, setSelectedStd] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [questionType, setQuestionType] = useState('mcq');
  const [loading, setLoading] = useState(true);

  // Data states
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [questions, setQuestions] = useState([]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateStdModal, setShowCreateStdModal] = useState(false);
  const [showCreateSubjectModal, setShowCreateSubjectModal] = useState(false);
  const [showCreateChapterModal, setShowCreateChapterModal] = useState(false);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showCreateTopicModal, setShowCreateTopicModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteItemInfo, setDeleteItemInfo] = useState({ id: null, type: null });
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [hasChildElements, setHasChildElements] = useState(false);

  // Form states
  const [selectedPoints, setSelectedPoints] = useState('all');
  
  const [newBoard, setNewBoard] = useState({ name: '', order: 1 });
const [newStandard, setNewStandard] = useState({ name: '', order: 1 });
const [newSubject, setNewSubject] = useState({ name: '', order: 1 });
const [newChapter, setNewChapter] = useState({ name: '', order: 1 });
const [newTopic, setNewTopic] = useState({ name: '', order: 1 });
const [editItemInfo, setEditItemInfo] = useState({ id: null, type: null, name: '', order: 1 });

  const [newQuestion, setNewQuestion] = useState({
    text: "",
    type: "mcq",
    points: 1,
    customPoints: "",
    options: [
      { text: "", image: null },
      { text: "", image: null },
      { text: "", image: null },
      { text: "", image: null }
    ],
    correctOption: 0,
    solution: "",
    solutionImages: [],
    boardId: "",
    standardId: "",
    subjectId: "",
    chapterId: "",
    topicId: "", // Add topicId
    hasImage: false,
    difficultyLevel: "Easy",
    images: []
  });

  // Firebase Storage file upload helper function
  const uploadFileToFirebase = async (file, folder = '') => {
    try {
      const fileName = folder ? `${folder}/${Date.now()}_${file.name}` : `${Date.now()}_${file.name}`;
      
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file to Firebase Storage:', error);
      throw error;
    }
  };

  // Firebase Storage file delete helper function
  const deleteFileFromFirebase = async (fileUrl) => {
    try {
      const fileRef = ref(storage, fileUrl);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file from Firebase Storage:', error);
    }
  };

  // Predefined lists
  const predefinedBoards = [
    "CBSE",
    "ICSE",
    "Maharashtra State Board",
    "Gujarat Board",
    "UP Board",
    "MP Board",
    "Rajasthan Board",
    "Bihar Board",
    "Tamil Nadu Board",
    "Karnataka Board"
  ];

  const predefinedStandards = [
    "Standard 11 (Science)",
    "Standard 12 (Science)"
  ];

  const predefinedSubjects = [
    "Mathematics",
    "Science",
    "English",
    "History",
    "Geography",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer",
    "Economics"
  ];

  const pointCategories = [1, 2, 3, 4, "Custom"];
  const mcqPointCategories = [1, 2, 3, 4, "Custom"];
  const descriptivePointCategories = [1, 2, 3, 4];

  // Function to open edit order modal
  const openEditOrderModal = (id, type, name, order) => {
    setEditItemInfo({
      id,
      type,
      name,
      order: order || 1
    });
    setShowEditOrderModal(true);
  };

  // Function to update order
  const handleUpdateOrder = async () => {
    try {
      const { id, type, order } = editItemInfo;
      
      if (!id || !type) return;
      
      // Create reference to the document based on type
      let itemRef;
      switch(type) {
        case 'board':
          itemRef = doc(db, 'boards', id);
          break;
        case 'standard':
          itemRef = doc(db, 'standards', id);
          break;
        case 'subject':
          itemRef = doc(db, 'subjects', id);
          break;
        case 'chapter':
          itemRef = doc(db, 'chapters', id);
          break;
        case 'topic':
          itemRef = doc(db, 'topics', id);
          break;
        default:
          console.error('Unknown item type:', type);
          return;
      }
      
      // Update the order
      await updateDoc(itemRef, { order: Math.max(1, order) });
      
      // Refresh the relevant list
      switch(type) {
        case 'board':
          const boardsRef = collection(db, "boards");
          const boardsSnapshot = await getDocs(boardsRef);
          const boardsList = boardsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          boardsList.sort((a, b) => (a.order || 1) - (b.order || 1));
          setBoards(boardsList);
          break;
          
        case 'standard':
          const standardsRef = collection(db, "standards");
          const standardsQuery = query(standardsRef, where("boardId", "==", selectedBoard));
          const standardsSnapshot = await getDocs(standardsQuery);
          const standardsList = standardsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          standardsList.sort((a, b) => (a.order || 0) - (b.order || 0));
          setStandards(standardsList);
          break;
          
        case 'subject':
          const subjectsRef = collection(db, "subjects");
          const subjectsQuery = query(subjectsRef, where("standardId", "==", selectedStd));
          const subjectsSnapshot = await getDocs(subjectsQuery);
          const subjectsList = subjectsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          subjectsList.sort((a, b) => (a.order || 0) - (b.order || 0));
          setSubjects(subjectsList);
          break;
          
        case 'chapter':
          const chaptersRef = collection(db, "chapters");
          const chaptersQuery = query(chaptersRef, where("subjectId", "==", selectedSubject));
          const chaptersSnapshot = await getDocs(chaptersQuery);
          const chaptersList = chaptersSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          chaptersList.sort((a, b) => (a.order || 0) - (b.order || 0));
          setChapters(chaptersList);
          break;
          
        case 'topic':
          const topicsRef = collection(db, "topics");
          const topicsQuery = query(topicsRef, where("chapterId", "==", selectedChapter));
          const topicsSnapshot = await getDocs(topicsQuery);
          const topicsList = topicsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          topicsList.sort((a, b) => (a.order || 0) - (b.order || 0));
          setTopics(topicsList);
          break;
      }
      
      // Close the modal
      setShowEditOrderModal(false);
      setEditItemInfo({ id: null, type: null, name: '', order: 0 });
    } catch (error) {
      console.error("Error updating order:", error);
      alert("Failed to update order. Please try again.");
    }
  };

  // Fetch boards on component mount
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const boardsRef = collection(db, "boards");
        const snapshot = await getDocs(boardsRef);
        const boardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort boards by order
        boardsList.sort((a, b) => (a.order || 1) - (b.order || 1));
        
        setBoards(boardsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching boards:", error);
        setLoading(false);
      }
    };
    fetchBoards();
  }, []);

  // Fetch standards when board is selected
  useEffect(() => {
    const fetchStandards = async () => {
      if (!selectedBoard) return;
      
      try {
        const standardsRef = collection(db, "standards");
        const q = query(standardsRef, where("boardId", "==", selectedBoard));
        const snapshot = await getDocs(q);
        const standardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort standards by order
        standardsList.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setStandards(standardsList);
      } catch (error) {
        console.error("Error fetching standards:", error);
      }
    };

    if (selectedBoard) {
      fetchStandards();
      setSelectedStd(null);
      setSelectedSubject(null);
      setSelectedChapter(null);
    }
  }, [selectedBoard]);

  // Fetch subjects when standard is selected
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!selectedStd) return;
    
      try {
        const subjectsRef = collection(db, "subjects");
        const q = query(subjectsRef, where("standardId", "==", selectedStd));
        const snapshot = await getDocs(q);
        const subjectsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort subjects by order
        subjectsList.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setSubjects(subjectsList);
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };    

    fetchSubjects();
  }, [selectedStd]);

  // Fetch chapters when subject is selected
  useEffect(() => {
    const fetchChapters = async () => {
      if (!selectedSubject) return;
    
      try {
        const chaptersRef = collection(db, "chapters");
        const q = query(chaptersRef, where("subjectId", "==", selectedSubject));
        const snapshot = await getDocs(q);
        const chaptersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort chapters by order
        chaptersList.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setChapters(chaptersList);
      } catch (error) {
        console.error("Error fetching chapters:", error);
      }
    };    

    fetchChapters();
  }, [selectedSubject]);

  // Fetch topics when chapter is selected
  useEffect(() => {
    const fetchTopics = async () => {
      if (!selectedChapter) return;
  
      console.log("Fetching topics for chapter:", selectedChapter);
  
      try {
        const topicsRef = collection(db, "topics");
        const q = query(topicsRef, where("chapterId", "==", selectedChapter));
        const snapshot = await getDocs(q);
        const topicsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort topics by order
        topicsList.sort((a, b) => (a.order || 0) - (b.order || 0));
  
        console.log("Fetched topics:", topicsList);
        setTopics(topicsList);
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    };
  
    fetchTopics();
  }, [selectedChapter]);  

  // Fetch questions when topic is selected
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedTopic) return;

      try {
        const questionsRef = collection(db, "questions");
        const q = query(questionsRef, where("topicId", "==", selectedTopic));
        const snapshot = await getDocs(q);
        const questionsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setQuestions(questionsList);
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, [selectedTopic]);

  const getCurrentPointCategories = () => {
    return questionType === 'mcq' ? mcqPointCategories : descriptivePointCategories;
  };

  // Create board
  const handleCreateBoard = async () => {
    try {
      if (!newBoard.name) {
        alert("Please select a board name.");
        return;
      }

      const boardsRef = collection(db, "boards");
      await addDoc(boardsRef, {
        name: newBoard.name,
        order: newBoard.order || 1, // Use the order provided by the user
        createdAt: new Date(),
      });

      const updatedBoardsRef = collection(db, "boards");
      const snapshot = await getDocs(updatedBoardsRef);
      const boardsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort boards by order
      boardsList.sort((a, b) => (a.order || 1) - (b.order || 1));
      
      setBoards(boardsList);
      setShowCreateBoardModal(false);
      setNewBoard({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating board:", error);
    }
  };

  // Create standard
  const handleCreateStandard = async () => {
    try {
      if (!selectedBoard) {
        alert("Please select a board first.");
        return;
      }

      if (!newStandard.name) {
        alert("Please select a standard name.");
        return;
      }

      const standardsRef = collection(db, "standards");
      await addDoc(standardsRef, {
        name: newStandard.name,
        boardId: selectedBoard,
        order: newStandard.order || 0, // Use the order provided by the user
        createdAt: new Date(),
      });

      const fetchStandards = async () => {
        const q = query(standardsRef, where("boardId", "==", selectedBoard));
        const snapshot = await getDocs(q);
        const standardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort standards by order
        standardsList.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setStandards(standardsList);
      };

      fetchStandards();
      setShowCreateStdModal(false);
      setNewStandard({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating standard:", error);
    }
  };

  // Create subject
  const handleCreateSubject = async () => {
    if (!selectedStd) {
      alert("Please select a standard first.");
      return;
    }
  
    if (!newSubject.name) {
      alert("Please select a subject name.");
      return;
    }
  
    try {
      const subjectsRef = collection(db, "subjects");
      await addDoc(subjectsRef, {
        name: newSubject.name,
        standardId: selectedStd,
        order: newSubject.order || 0, // Use the order provided by the user
        createdAt: new Date(),
      });
  
      const fetchSubjects = async () => {
        const q = query(subjectsRef, where("standardId", "==", selectedStd));
        const snapshot = await getDocs(q);
        const subjectsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort subjects by order
        subjectsList.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setSubjects(subjectsList);
      };

      fetchSubjects();
      setShowCreateSubjectModal(false);
      setNewSubject({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating subject:", error);
    }
  };

  // Create chapter
  const handleCreateChapter = async () => {
    if (!selectedSubject) {
      alert("Please select a subject first.");
      return;
    }

    if (!newChapter.name.trim()) {
      alert("Chapter name cannot be empty.");
      return;
    }
  
    try {
      const chaptersRef = collection(db, "chapters");
      await addDoc(chaptersRef, {
        name: newChapter.name,
        subjectId: selectedSubject,
        order: newChapter.order || 0, // Use the order provided by the user
        createdAt: new Date(),
      });
  
      const fetchChapters = async () => {
        const q = query(chaptersRef, where("subjectId", "==", selectedSubject));
        const snapshot = await getDocs(q);
        const chaptersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort chapters by order
        chaptersList.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        setChapters(chaptersList);
      };

      fetchChapters();
      setShowCreateChapterModal(false);
      setNewChapter({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating chapter:", error);
    }
  };

  // Create topic
  const handleCreateTopic = async () => {
    console.log("Create Topic button clicked, attempting to create topic...");
  
    if (!selectedChapter) {
      alert("Please select a chapter first.");
      console.log("Create Topic failed: No chapter selected.");
      return;
    }
  
    if (!newTopic.name.trim()) {
      alert("Topic name cannot be empty.");
      console.log("Create Topic failed: Topic name is empty.");
      return;
    }
  
    try {
      console.log("Saving topic to Firestore...");
      const topicsRef = collection(db, "topics");
      const docRef = await addDoc(topicsRef, {
        name: newTopic.name,
        chapterId: selectedChapter,
        order: newTopic.order || 0, // Use the order provided by the user
        createdAt: new Date(),
      });
  
      console.log("Topic created with ID:", docRef.id);
  
      // Fetch the updated topics list
      const snapshot = await getDocs(query(topicsRef, where("chapterId", "==", selectedChapter)));
      const topicsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort topics by order
      topicsList.sort((a, b) => (a.order || 0) - (b.order || 0));
  
      console.log("Updated topics list after adding:", topicsList);
      setTopics(topicsList);
  
      setShowCreateTopicModal(false);
      setNewTopic({ name: "", order: 0 });
    } catch (error) {
      console.error("Error creating topic:", error);
      alert("Failed to create topic. Check console for details.");
    }
  };  
  
  // Handle adding new question - Updated for Firebase Storage
  const handleAddQuestion = async () => {
    // Validation for required fields
    if (!newQuestion.text.trim()) {
        alert("Please enter the question text.");
        return;
    }
    if (!selectedBoard) {
        alert("Please select a board.");
        return;
    }
    if (!selectedStd) {
        alert("Please select a standard.");
        return;
    }
    if (!selectedSubject) {
        alert("Please select a subject.");
        return;
    }
    if (!selectedChapter) {
        alert("Please select a chapter.");
        return;
    }
    if (!selectedTopic) {
        alert("Please select a topic.");
        return;
    }
    if (!newQuestion.points && newQuestion.customPoints === "") {
        alert("Please select a valid point category.");
        return;
    }

    if (newQuestion.type === "mcq") {
        if (newQuestion.options.some((option) => (!option.text || option.text.trim() === "") && !option.image)) {
            alert("Each MCQ option must have either text or an image.");
            return;
        }

        if (newQuestion.correctOption === null || newQuestion.correctOption < 0 || newQuestion.correctOption >= newQuestion.options.length) {
            alert("Please select a correct option.");
            return;
        }
    }

    if (!newQuestion.solution.trim() && newQuestion.solutionImages.length === 0) {
        alert("Please provide a solution or upload a solution image.");
        return;
    }

    let uploadedQuestionImages = [];
    let uploadedOptionImages = [];
    let uploadedSolutionImages = [];

    try {
        setLoading(true);

        // Upload Question Images using Firebase Storage (if any)
        if (newQuestion.hasImage && newQuestion.images.length > 0) {
            const uploadPromises = newQuestion.images.map(async (image) => {
                return await uploadFileToFirebase(image, 'questions');
            });

            uploadedQuestionImages = await Promise.all(uploadPromises);
        }

        // Upload MCQ Option Images using Firebase Storage (if any)
        if (newQuestion.type === "mcq") {
            const optionUploadPromises = newQuestion.options.map(async (option) => {
                if (option.image) {
                    return await uploadFileToFirebase(option.image, 'options');
                }
                return null;
            });

            uploadedOptionImages = await Promise.all(optionUploadPromises);

            // Update options with uploaded image URLs
            newQuestion.options = newQuestion.options.map((option, index) => ({
                text: option.text,
                image: uploadedOptionImages[index] || null,
            }));
        }

        // Upload Solution Images using Firebase Storage (if any)
        if (newQuestion.solutionImages.length > 0) {
            const solutionImageUploadPromises = newQuestion.solutionImages.map(async (image) => {
                return await uploadFileToFirebase(image, 'solutions');
            });

            uploadedSolutionImages = await Promise.all(solutionImageUploadPromises);
        }

        // Automatically set the answer key (Store as a number)
        const correctOptionIndex = newQuestion.correctOption;
        const answerKey = correctOptionIndex;

        // Ensuring all required fields have values before saving to Firestore
        const sanitizeData = (obj) => {
            return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
        };

        // Prepare question data for Firestore
        const questionData = sanitizeData({
            question: {  
                text: newQuestion.text,
                images: uploadedQuestionImages || [], 
            },
            type: newQuestion.type,
            points: newQuestion.points === "" ? newQuestion.customPoints : newQuestion.points,
            solution: newQuestion.solution,
            solutionImages: uploadedSolutionImages || [], 
            correctOption: correctOptionIndex, 
            answerKey: answerKey,
            difficultyLevel: newQuestion.difficultyLevel,
            questionYear: newQuestion.questionYear,
            boardId: selectedBoard,
            standardId: selectedStd,
            subjectId: selectedSubject,
            chapterId: selectedChapter,
            topicId: selectedTopic,
            options: newQuestion.options?.map(opt => ({
                text: opt.text !== undefined ? opt.text : null, 
                image: opt.image !== undefined ? opt.image : null
            })),
            createdAt: new Date(),
        });

        // Upload Question Data to Firestore
        const questionsRef = collection(db, "questions");
        await addDoc(questionsRef, questionData);

        // Refresh Questions List
        const fetchQuestions = async () => {
            const q = query(questionsRef, where("topicId", "==", selectedTopic));
            const snapshot = await getDocs(q);
            const questionsList = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setQuestions(questionsList);
        };

        fetchQuestions();

        // Reset Form
        setNewQuestion({
            text: "",
            type: "mcq",
            points: 1,
            customPoints: "",
            options: [
                { text: "", image: null },
                { text: "", image: null },
                { text: "", image: null },
                { text: "", image: null },
            ],
            correctOption: 0,
            solution: "",
            solutionImages: [],
            answerKey: "",
            difficultyLevel: "Easy", 
            questionYear: "",
            hasImage: false,
            images: []
        });

        setShowAddModal(false);
        alert("Question added successfully!");
    } catch (error) {
        console.error("Error adding question:", error);
        alert("An error occurred while adding the question.");
    } finally {
        setLoading(false);
    }
  };
  
  
  // Function to check for child elements
  const checkForChildElements = async (id, type) => {
    try {
      let hasChildren = false;
      
      switch(type) {
        case 'board':
          const standardsQuery = query(collection(db, 'standards'), where('boardId', '==', id));
          const standardsSnapshot = await getDocs(standardsQuery);
          hasChildren = standardsSnapshot.docs.length > 0;
          break;
          
        case 'standard':
          const subjectsQuery = query(collection(db, 'subjects'), where('standardId', '==', id));
          const subjectsSnapshot = await getDocs(subjectsQuery);
          hasChildren = subjectsSnapshot.docs.length > 0;
          break;
          
        case 'subject':
          const chaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', id));
          const chaptersSnapshot = await getDocs(chaptersQuery);
          hasChildren = chaptersSnapshot.docs.length > 0;
          break;
          
        case 'chapter':
          const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', id));
          const topicsSnapshot = await getDocs(topicsQuery);
          hasChildren = topicsSnapshot.docs.length > 0;
          break;
          
        case 'topic':
          const questionsQuery = query(collection(db, 'questions'), where('topicId', '==', id));
          const questionsSnapshot = await getDocs(questionsQuery);
          hasChildren = questionsSnapshot.docs.length > 0;
          break;
          
        default:
          hasChildren = false;
      }
      
      return hasChildren;
    } catch (error) {
      console.error('Error checking child elements:', error);
      return false;
    }
  };

  // Function to handle cascade deletion
  const handleCascadeDelete = async (id, type) => {
    const batch = writeBatch(db);
    
    try {
      switch(type) {
        case 'board':
          // Delete all standards, subjects, chapters, topics, and questions for this board
          const standardsQuery = query(collection(db, 'standards'), where('boardId', '==', id));
          const standardsSnapshot = await getDocs(standardsQuery);
          
          for (const standardDoc of standardsSnapshot.docs) {
            const standardId = standardDoc.id;
            
            // Delete subjects under this standard
            const subjectsQuery = query(collection(db, 'subjects'), where('standardId', '==', standardId));
            const subjectsSnapshot = await getDocs(subjectsQuery);
            
            for (const subjectDoc of subjectsSnapshot.docs) {
              const subjectId = subjectDoc.id;
              
              // Delete chapters under this subject
              const chaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', subjectId));
              const chaptersSnapshot = await getDocs(chaptersQuery);
              
              for (const chapterDoc of chaptersSnapshot.docs) {
                const chapterId = chapterDoc.id;
                
                // Delete topics under this chapter
                const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', chapterId));
                const topicsSnapshot = await getDocs(topicsQuery);
                
                for (const topicDoc of topicsSnapshot.docs) {
                  const topicId = topicDoc.id;
                  
                  // Delete questions under this topic
                  const questionsQuery = query(collection(db, 'questions'), where('topicId', '==', topicId));
                  const questionsSnapshot = await getDocs(questionsQuery);
                  
                  questionsSnapshot.docs.forEach(questionDoc => {
                    batch.delete(questionDoc.ref);
                  });
                  
                  batch.delete(topicDoc.ref);
                }
                
                batch.delete(chapterDoc.ref);
              }
              
              batch.delete(subjectDoc.ref);
            }
            
            batch.delete(standardDoc.ref);
          }
          
          batch.delete(doc(db, 'boards', id));
          break;
          
        case 'standard':
          // Similar cascade logic for standard
          const stdSubjectsQuery = query(collection(db, 'subjects'), where('standardId', '==', id));
          const stdSubjectsSnapshot = await getDocs(stdSubjectsQuery);
          
          for (const subjectDoc of stdSubjectsSnapshot.docs) {
            const subjectId = subjectDoc.id;
            
            const chaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', subjectId));
            const chaptersSnapshot = await getDocs(chaptersQuery);
            
            for (const chapterDoc of chaptersSnapshot.docs) {
              const chapterId = chapterDoc.id;
              
              const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', chapterId));
              const topicsSnapshot = await getDocs(topicsQuery);
              
              for (const topicDoc of topicsSnapshot.docs) {
                const topicId = topicDoc.id;
                
                const questionsQuery = query(collection(db, 'questions'), where('topicId', '==', topicId));
                const questionsSnapshot = await getDocs(questionsQuery);
                
                questionsSnapshot.docs.forEach(questionDoc => {
                  batch.delete(questionDoc.ref);
                });
                
                batch.delete(topicDoc.ref);
              }
              
              batch.delete(chapterDoc.ref);
            }
            
            batch.delete(subjectDoc.ref);
          }
          
          batch.delete(doc(db, 'standards', id));
          break;
          
        case 'subject':
          // Similar cascade logic for subject
          const subChaptersQuery = query(collection(db, 'chapters'), where('subjectId', '==', id));
          const subChaptersSnapshot = await getDocs(subChaptersQuery);
          
          for (const chapterDoc of subChaptersSnapshot.docs) {
            const chapterId = chapterDoc.id;
            
            const topicsQuery = query(collection(db, 'topics'), where('chapterId', '==', chapterId));
            const topicsSnapshot = await getDocs(topicsQuery);
            
            for (const topicDoc of topicsSnapshot.docs) {
              const topicId = topicDoc.id;
              
              const questionsQuery = query(collection(db, 'questions'), where('topicId', '==', topicId));
              const questionsSnapshot = await getDocs(questionsQuery);
              
              questionsSnapshot.docs.forEach(questionDoc => {
                batch.delete(questionDoc.ref);
              });
              
              batch.delete(topicDoc.ref);
            }
            
            batch.delete(chapterDoc.ref);
          }
          
          batch.delete(doc(db, 'subjects', id));
          break;
          
        case 'chapter':
          // Similar cascade logic for chapter
          const chapTopicsQuery = query(collection(db, 'topics'), where('chapterId', '==', id));
          const chapTopicsSnapshot = await getDocs(chapTopicsQuery);
          
          for (const topicDoc of chapTopicsSnapshot.docs) {
            const topicId = topicDoc.id;
            
            const questionsQuery = query(collection(db, 'questions'), where('topicId', '==', topicId));
            const questionsSnapshot = await getDocs(questionsQuery);
            
            questionsSnapshot.docs.forEach(questionDoc => {
              batch.delete(questionDoc.ref);
            });
            
            batch.delete(topicDoc.ref);
          }
          
          batch.delete(doc(db, 'chapters', id));
          break;
          
        case 'topic':
          // Delete all questions under this topic
          const topQuestionsQuery = query(collection(db, 'questions'), where('topicId', '==', id));
          const topQuestionsSnapshot = await getDocs(topQuestionsQuery);
          
          topQuestionsSnapshot.docs.forEach(questionDoc => {
            batch.delete(questionDoc.ref);
          });
          
          batch.delete(doc(db, 'topics', id));
          break;
          
        case 'question':
          batch.delete(doc(db, 'questions', id));
          break;
      }
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error in cascade delete:', error);
      return false;
    }
  };

 const handleConfirmDelete = async (forceDelete = false) => {
    try {
      const { id, type } = deleteItemInfo;
      
      if (!id || !type) return;
      
      // If item has children and no force delete, show password modal
      if (hasChildElements && !forceDelete) {
        setShowDeleteConfirmation(false);
        setShowPasswordModal(true);
        return;
      }
      
      const success = await handleCascadeDelete(id, type);
      
      if (success) {
        // Update local state based on what was deleted
        switch(type) {
          case 'board':
            setBoards(boards.filter(board => board.id !== id));
            if (selectedBoard === id) {
              setSelectedBoard(null);
              setSelectedStd(null);
              setSelectedSubject(null);
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'standard':
            setStandards(standards.filter(std => std.id !== id));
            if (selectedStd === id) {
              setSelectedStd(null);
              setSelectedSubject(null);
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'subject':
            setSubjects(subjects.filter(subject => subject.id !== id));
            if (selectedSubject === id) {
              setSelectedSubject(null);
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'chapter':
            setChapters(chapters.filter(chapter => chapter.id !== id));
            if (selectedChapter === id) {
              setSelectedChapter(null);
              setSelectedTopic(null);
            }
            break;
            
          case 'topic':
            setTopics(topics.filter(topic => topic.id !== id));
            if (selectedTopic === id) {
              setSelectedTopic(null);
            }
            break;
            
          case 'question':
            setQuestions(questions.filter(q => q.id !== id));
            break;
        }
        
        alert(`Successfully deleted ${type} and all related data.`);
      } else {
        alert(`Failed to delete ${type}. Please try again.`);
      }
      
      // Close the modals and reset state
      setShowDeleteConfirmation(false);
      setShowPasswordModal(false);
      setDeleteItemInfo({ id: null, type: null });
      setDeletePassword('');
      setHasChildElements(false);
    } catch (error) {
      console.error(`Error deleting ${deleteItemInfo.type}:`, error);
      alert(`Failed to delete ${deleteItemInfo.type}. Please try again.`);
    }
  };

  // Handle password verification for force delete
  const handlePasswordVerification = () => {
    if (deletePassword === '369874') {
      handleConfirmDelete(true);
    } else {
      alert('Incorrect password. Cannot proceed with deletion.');
      setDeletePassword('');
    }
  };
  
  const requestDeleteTopic = async (topicId) => {
    const hasChildren = await checkForChildElements(topicId, 'topic');
    setDeleteItemInfo({ id: topicId, type: 'topic' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteQuestion = async (questionId) => {
    setDeleteItemInfo({ id: questionId, type: 'question' });
    setHasChildElements(false); // Questions have no children
    setShowDeleteConfirmation(true);
  };

  const requestDeleteChapter = async (chapterId) => {
    const hasChildren = await checkForChildElements(chapterId, 'chapter');
    setDeleteItemInfo({ id: chapterId, type: 'chapter' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteBoard = async (boardId) => {
    const hasChildren = await checkForChildElements(boardId, 'board');
    setDeleteItemInfo({ id: boardId, type: 'board' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteStandard = async (standardId) => {
    const hasChildren = await checkForChildElements(standardId, 'standard');
    setDeleteItemInfo({ id: standardId, type: 'standard' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const requestDeleteSubject = async (subjectId) => {
    const hasChildren = await checkForChildElements(subjectId, 'subject');
    setDeleteItemInfo({ id: subjectId, type: 'subject' });
    setHasChildElements(hasChildren);
    setShowDeleteConfirmation(true);
  };

  const handleOpenEditModal = (question) => {
    console.log("Opening edit modal for question:", question);
    setEditingQuestion({
      ...question,
      text: question.question?.text || question.text || "",
      existingImages: question.question?.images || question.images || [],
      existingSolutionImages: question.solutionImages || [],
      existingOptionImages: question.options?.map(opt => opt.image) || [],
      newImages: [],
      newSolutionImages: [],
      customPoints: question.points > 4 ? question.points : ""
    });
    setShowEditModal(true);
  };

  const handleEditQuestion = async () => {
    try {
        if (!editingQuestion) return;

        let updatedQuestionImages = [...(editingQuestion.existingImages || [])];
        let updatedSolutionImages = [...(editingQuestion.existingSolutionImages || [])];
        let updatedOptionImages = [...(editingQuestion.existingOptionImages || [])];

        // Upload new question images if added using Firebase Storage
        if (editingQuestion.hasImage && editingQuestion.newImages?.length > 0) {
            const uploadPromises = editingQuestion.newImages.map(async (image) => {
                return await uploadFileToFirebase(image, 'questions');
            });

            const newUploadedImages = await Promise.all(uploadPromises);
            updatedQuestionImages = [...updatedQuestionImages, ...newUploadedImages];
        }

        // Upload new solution images if added using Firebase Storage
        if (editingQuestion.newSolutionImages?.length > 0) {
            const solutionUploadPromises = editingQuestion.newSolutionImages.map(async (image) => {
                return await uploadFileToFirebase(image, 'solutions');
            });

            const newUploadedSolutionImages = await Promise.all(solutionUploadPromises);
            updatedSolutionImages = [...updatedSolutionImages, ...newUploadedSolutionImages];
        }

        // Upload new MCQ option images if added using Firebase Storage
        const optionUploadPromises = editingQuestion.options.map(async (option) => {
            if (option.image && typeof option.image !== "string") {
                return await uploadFileToFirebase(option.image, 'options');
            }
            return option.image || null;
        });

        updatedOptionImages = await Promise.all(optionUploadPromises);

        // Update the options array with new image URLs
        const updatedOptions = editingQuestion.options.map((option, index) => ({
            text: option.text,
            image: updatedOptionImages[index] || null,
        }));

        // Automatically set the answer key based on the correct option (Store as a number)
        const correctOptionIndex = editingQuestion.correctOption;
        const answerKey = correctOptionIndex; // Store answer key as a number (0,1,2,3)

        // Prepare updated data for Firestore
        const updatedQuestionData = {
            question: {
                text: editingQuestion.text || "",
                images: updatedQuestionImages || [],
            },
            type: editingQuestion.type || "mcq",
            points: editingQuestion.points === "" ? (editingQuestion.customPoints || 1) : editingQuestion.points,
            options: updatedOptions || [],
            correctOption: correctOptionIndex,
            answerKey: answerKey,
            solution: editingQuestion.solution || "",
            solutionImages: updatedSolutionImages || [],
            difficultyLevel: editingQuestion.difficultyLevel || "Easy",
            questionYear: editingQuestion.questionYear || "",
            updatedAt: new Date(),
        };

        // Update Firestore document
        const questionRef = doc(db, "questions", editingQuestion.id);
        await updateDoc(questionRef, updatedQuestionData);

        // Refresh questions list
        const questionsRef = collection(db, "questions");
        const q = query(questionsRef, where("topicId", "==", selectedTopic));
        const snapshot = await getDocs(q);
        const updatedQuestions = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
        setQuestions(updatedQuestions);

        // Close modal and reset state
        setShowEditModal(false);
        setEditingQuestion(null);
        alert("Question updated successfully!");
    } catch (error) {
        console.error("Error updating question:", error);
        alert("Failed to update the question. Please try again.");
    }
};

return (
  <div className="min-h-screen bg-gray-50">
    {/* Header */}
    <header className="bg-white border-b border-gray-200 px-4 py-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Question Management</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          disabled={!selectedTopic}
        >
          <PlusCircle className="w-5 h-5" />
          Add Question
        </button>
      </div>
    </header>

    <div className="flex">
      {/* Sidebar Navigation */}
      <div className="w-72 bg-gradient-to-b from-slate-50 to-white h-[calc(100vh-80px)] border-r border-slate-200 shadow-sm overflow-y-auto">
        <div className="p-6">
        {/* Boards Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
              <h2 className="text-lg font-bold text-slate-800">Education Boards</h2>
            </div>
            <button
              onClick={() => setShowCreateBoardModal(true)}
              className="p-2 hover:bg-blue-50 rounded-xl transition-all duration-200 group"
              title="Add New Board"
            >
              <Plus className="w-5 h-5 text-blue-600 group-hover:text-blue-700" />
            </button>
          </div>
          <div className="space-y-3">
            {boards.map(board => (
              <div 
                key={board.id} 
                className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                  selectedBoard === board.id 
                    ? 'border-blue-400 shadow-lg shadow-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <button
                  onClick={() => {
                    setSelectedBoard(board.id);
                    setSelectedStd(null);
                    setSelectedSubject(null);
                    setSelectedChapter(null);
                  }}
                  className="w-full text-left p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        selectedBoard === board.id ? 'bg-blue-100' : 'bg-slate-100 group-hover:bg-slate-200'
                      }`}>
                        <Building className={`w-4 h-4 ${
                          selectedBoard === board.id ? 'text-blue-600' : 'text-slate-600'
                        }`} />
                      </div>
                      <div>
                        <span className={`font-medium ${
                          selectedBoard === board.id ? 'text-blue-900' : 'text-slate-800'
                        }`}>
                          {board.name}
                        </span>
                        <div className="text-xs text-slate-500 mt-1">
                          Position: {board.order || 1}
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
                
                {/* Action buttons */}
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditOrderModal(board.id, 'board', board.name, board.order);
                    }}
                    className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                    title="Edit order"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      requestDeleteBoard(board.id);
                    }}
                    className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    title="Delete board"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Standards Section - Only show if board is selected */}
        {selectedBoard && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full"></div>
                <h2 className="text-lg font-bold text-slate-800">Class Standards</h2>
              </div>
              <button
                onClick={() => setShowCreateStdModal(true)}
                className="p-2 hover:bg-emerald-50 rounded-xl transition-all duration-200 group"
                title="Add New Standard"
              >
                <Plus className="w-5 h-5 text-emerald-600 group-hover:text-emerald-700" />
              </button>
            </div>
            <div className="space-y-3">
              {standards.map(std => (
                <div 
                  key={std.id} 
                  className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                    selectedStd === std.id 
                      ? 'border-emerald-400 shadow-lg shadow-emerald-100 bg-gradient-to-r from-emerald-50 to-green-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <button
                    onClick={() => {
                      setSelectedStd(std.id);
                      setSelectedSubject(null);
                      setSelectedChapter(null);
                    }}
                    className="w-full text-left p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          selectedStd === std.id ? 'bg-emerald-100' : 'bg-slate-100 group-hover:bg-slate-200'
                        }`}>
                          <Book className={`w-4 h-4 ${
                            selectedStd === std.id ? 'text-emerald-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <span className={`font-medium ${
                            selectedStd === std.id ? 'text-emerald-900' : 'text-slate-800'
                          }`}>
                            {std.name}
                          </span>
                          <div className="text-xs text-slate-500 mt-1">
                            Position: {std.order || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Action buttons */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditOrderModal(std.id, 'standard', std.name, std.order);
                      }}
                      className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                      title="Edit order"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDeleteStandard(std.id);
                      }}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete standard"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subjects Section - Only show if standard is selected */}
        {selectedStd && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full"></div>
                <h2 className="text-lg font-bold text-slate-800">Subjects</h2>
              </div>
              <button
                onClick={() => setShowCreateSubjectModal(true)}
                className="p-2 hover:bg-purple-50 rounded-xl transition-all duration-200 group"
                title="Add New Subject"
              >
                <Plus className="w-5 h-5 text-purple-600 group-hover:text-purple-700" />
              </button>
            </div>
            <div className="space-y-3">
              {subjects.map(subject => (
                <div 
                  key={subject.id} 
                  className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                    selectedSubject === subject.id 
                      ? 'border-purple-400 shadow-lg shadow-purple-100 bg-gradient-to-r from-purple-50 to-violet-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <button
                    onClick={() => {
                      setSelectedSubject(subject.id);
                      setSelectedChapter(null);
                    }}
                    className="w-full text-left p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          selectedSubject === subject.id ? 'bg-purple-100' : 'bg-slate-100 group-hover:bg-slate-200'
                        }`}>
                          <BookOpen className={`w-4 h-4 ${
                            selectedSubject === subject.id ? 'text-purple-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <span className={`font-medium ${
                            selectedSubject === subject.id ? 'text-purple-900' : 'text-slate-800'
                          }`}>
                            {subject.name}
                          </span>
                          <div className="text-xs text-slate-500 mt-1">
                            Position: {subject.order || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Action buttons */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditOrderModal(subject.id, 'subject', subject.name, subject.order);
                      }}
                      className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                      title="Edit order"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDeleteSubject(subject.id);
                      }}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete subject"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chapters Section - Only show if subject is selected */}
        {selectedSubject && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full"></div>
                <h2 className="text-lg font-bold text-slate-800">Chapters</h2>
              </div>
              <button
                onClick={() => setShowCreateChapterModal(true)}
                className="p-2 hover:bg-orange-50 rounded-xl transition-all duration-200 group"
                title="Add New Chapter"
              >
                <Plus className="w-5 h-5 text-orange-600 group-hover:text-orange-700" />
              </button>
            </div>
            <div className="space-y-3">
              {chapters.map(chapter => (
                <div 
                  key={chapter.id} 
                  className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                    selectedChapter === chapter.id 
                      ? 'border-orange-400 shadow-lg shadow-orange-100 bg-gradient-to-r from-orange-50 to-amber-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <button
                    onClick={() => {
                      setSelectedChapter(chapter.id);
                      setSelectedTopic(null);
                    }}
                    className="w-full text-left p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          selectedChapter === chapter.id ? 'bg-orange-100' : 'bg-slate-100 group-hover:bg-slate-200'
                        }`}>
                          <ListOrdered className={`w-4 h-4 ${
                            selectedChapter === chapter.id ? 'text-orange-600' : 'text-slate-600'
                          }`} />
                        </div>
                        <div>
                          <span className={`font-medium ${
                            selectedChapter === chapter.id ? 'text-orange-900' : 'text-slate-800'
                          }`}>
                            {chapter.name}
                          </span>
                          <div className="text-xs text-slate-500 mt-1">
                            Position: {chapter.order || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Action buttons */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditOrderModal(chapter.id, 'chapter', chapter.name, chapter.order);
                      }}
                      className="p-1.5 text-slate-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                      title="Edit order"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDeleteChapter(chapter.id);
                      }}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete chapter"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Topics Section - Only show if chapter is selected */}
        {selectedChapter && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-6 bg-gradient-to-b from-teal-500 to-teal-600 rounded-full"></div>
                <h2 className="text-lg font-bold text-slate-800">Topics</h2>
              </div>
              <button
                onClick={() => {
                  console.log("Create Topic button clicked - opening modal");
                  setShowCreateTopicModal(true);
                }}
                className="p-2 hover:bg-teal-50 rounded-xl transition-all duration-200 group"
                title="Add New Topic"
              >
                <Plus className="w-5 h-5 text-teal-600 group-hover:text-teal-700" />
              </button>
            </div>
            
            <div className="space-y-3">
              {topics.length > 0 ? (
                topics.map(topic => (
                  <div 
                    key={topic.id} 
                    className={`group relative bg-white border-2 transition-all duration-200 rounded-xl hover:shadow-md ${
                      selectedTopic === topic.id 
                        ? 'border-teal-400 shadow-lg shadow-teal-100 bg-gradient-to-r from-teal-50 to-cyan-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <button
                      onClick={() => setSelectedTopic(topic.id)}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            selectedTopic === topic.id ? 'bg-teal-100' : 'bg-slate-100 group-hover:bg-slate-200'
                          }`}>
                            <ListOrdered className={`w-4 h-4 ${
                              selectedTopic === topic.id ? 'text-teal-600' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <span className={`font-medium ${
                              selectedTopic === topic.id ? 'text-teal-900' : 'text-slate-800'
                            }`}>
                              {topic.name}
                            </span>
                            <div className="text-xs text-slate-500 mt-1">
                              Position: {topic.order || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                    
                    {/* Action buttons */}
                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditOrderModal(topic.id, 'topic', topic.name, topic.order);
                        }}
                        className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                        title="Edit order"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteTopic(topic.id);
                        }}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete topic"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-slate-400 mb-2">
                    <ListOrdered className="w-8 h-8 mx-auto" />
                  </div>
                  <p className="text-slate-500 text-sm">No topics found</p>
                  <p className="text-slate-400 text-xs">Add a new topic using the '+' button</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>


      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {hasChildElements ? 'Cannot Delete - Has Child Elements' : 'Confirm Delete'}
            </h3>
            
            {hasChildElements ? (
              <div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-800">Protection Enabled</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        This {deleteItemInfo.type} contains child elements. You must delete all child elements first, or use "Direct Delete" with admin password.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteItemInfo({ id: null, type: null });
                      setHasChildElements(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleConfirmDelete()}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    Direct Delete (Requires Password)
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-gray-700 mb-6">
                  Are you sure you want to delete this {deleteItemInfo.type}? This action cannot be undone.
                </p>
                
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      setDeleteItemInfo({ id: null, type: null });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Password Modal for Force Delete */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-4">⚠️ Direct Delete - Admin Access Required</h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>WARNING:</strong> This will permanently delete the {deleteItemInfo.type} and ALL its child elements (subjects, chapters, topics, questions, etc.). This action cannot be undone.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Admin Password:
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter password to proceed"
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordVerification()}
              />
            </div>
            
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setDeletePassword('');
                  setDeleteItemInfo({ id: null, type: null });
                  setHasChildElements(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordVerification}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                disabled={!deletePassword.trim()}
              >
                Confirm Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Edit Display Order for {editItemInfo.name}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                type="number"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={editItemInfo.order}
                onChange={(e) => setEditItemInfo({...editItemInfo, order: parseInt(e.target.value) || 0})}
                min="0"
              />
              <p className="mt-1 text-sm text-gray-500">
                Items are displayed in ascending order (1, 2, 3,...)
              </p>
            </div>
            
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowEditOrderModal(false);
                  setEditItemInfo({ id: null, type: null, name: '', order: 0 });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateOrder}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 p-6">
        {selectedTopic && (
          <>
            {/* Question Type Tabs */}
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setQuestionType("mcq")}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      questionType === "mcq"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    MCQ Questions
                  </button>
                  <button
                    onClick={() => setQuestionType("descriptive")}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      questionType === "descriptive"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Descriptive Questions
                  </button>
                </nav>
              </div>
            </div>

            {/* Questions Section */}
            {pointCategories.map((point) => {
              const filteredQuestions = questions.filter(
                (q) =>
                  q.type === questionType &&
                  (q.points === point || (point === "Custom" && q.points > 4))
              );

              if (filteredQuestions.length === 0) return null;

              return (
                <div key={point} className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {point === "Custom" ? "Custom Points" : `${point} Point Questions`}
                  </h3>

                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Question
                          </th>
                          {questionType === "mcq" && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Options
                            </th>
                          )}
                          {questionType === "descriptive" && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Solution
                            </th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Points
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredQuestions.map((question) => (
                          <tr key={question.id}>
                            <td className="px-6 py-4">
                              {/* Ensure question object exists before accessing text */}
                              <div className="text-sm text-gray-900">
                                {question?.question?.text || "No text available"}
                              </div>

                              {/* Ensure images exist before rendering */}
                              {question?.question?.images && question.question.images.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {question.question.images.map((img, index) => (
                                    <img
                                      key={index}
                                      src={img}
                                      alt={`Question Image ${index + 1}`}
                                      className="w-20 h-20 object-cover rounded-md border"
                                    />
                                  ))}
                                </div>
                              )}
                            </td>

                            {questionType === "mcq" && (
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-500">
                                  {question.options?.map((option, index) => (
                                    <div
                                      key={index}
                                      className={`${
                                        index === question.correctOption
                                          ? "text-green-600 font-medium"
                                          : ""
                                      }`}
                                    >
                                      {index + 1}. {option.text}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            )}

                            {questionType === "descriptive" && (
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-500">
                                  {question.solution}
                                </div>
                              </td>
                            )}

                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {question.points}
                              </div>
                            </td>

                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                <button className="text-blue-600 hover:text-blue-800">
                                  <Eye className="w-5 h-5" />
                                </button>
                                <button 
                                  onClick={() => handleOpenEditModal(question)}
                                  className="text-yellow-600 hover:text-yellow-800"
                                >
                                  <Edit className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => requestDeleteQuestion(question.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>

    {/* Create Board Modal */}
    {showCreateBoardModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Create New Board</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Board Name
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg overflow-y-auto"
                  value={newBoard.name}
                  onChange={(e) =>
                    setNewBoard({ ...newBoard, name: e.target.value })
                  }
                  size="5"
                >
                  {predefinedBoards.map((board, index) => (
                    <option key={index} value={board}>
                      {board}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Add order input field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Enter display order (e.g., 1, 2, 3...)"
                  value={newBoard.order}
                  onChange={(e) => 
                    setNewBoard({ ...newBoard, order: parseInt(e.target.value) || 1 })
                  }
                  min="0"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setShowCreateBoardModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBoard}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Board
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Create Standard Modal */}
    {showCreateStdModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Create New Standard</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Standard Name
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg overflow-y-auto"
                  value={newStandard.name}
                  onChange={(e) =>
                    setNewStandard({ ...newStandard, name: e.target.value })
                  }
                  size="5"
                >
                  {predefinedStandards.map((std, index) => (
                    <option key={index} value={std}>
                      {std}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Add order input field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Enter display order (e.g., 1, 2, 3...)"
                  value={newStandard.order}
                  onChange={(e) => 
                    setNewStandard({ ...newStandard, order: parseInt(e.target.value) || 0 })
                  }
                  min="0"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setShowCreateStdModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateStandard}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Standard
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Create Subject Modal */}
    {showCreateSubjectModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Create New Subject</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Subject Name
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg overflow-y-auto"
                  value={newSubject.name}
                  onChange={(e) =>
                    setNewSubject({ ...newSubject, name: e.target.value })
                  }
                  size="5"
                >
                  {predefinedSubjects.map((subject, index) => (
                    <option key={index} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Add order input field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Enter display order (e.g., 1, 2, 3...)"
                  value={newSubject.order}
                  onChange={(e) => 
                    setNewSubject({ ...newSubject, order: parseInt(e.target.value) || 0 })
                  }
                  min="0"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setShowCreateSubjectModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSubject}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Subject
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Create Chapter Modal */}
    {showCreateChapterModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Create New Chapter</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chapter Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Chapter 1: Introduction"
                  value={newChapter.name}
                  onChange={(e) => setNewChapter({ ...newChapter, name: e.target.value })}
                />
              </div>
              
              {/* Add order input field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Enter display order (e.g., 1, 2, 3...)"
                  value={newChapter.order}
                  onChange={(e) => 
                    setNewChapter({ ...newChapter, order: parseInt(e.target.value) || 0 })
                  }
                  min="0"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setShowCreateChapterModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateChapter}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Chapter
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Create Topic Modal */}
    {showCreateTopicModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Create New Topic</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Topic Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Enter topic name..."
                  value={newTopic.name}
                  onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
                />
              </div>
              
              {/* Add order input field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  placeholder="Enter display order (e.g., 1, 2, 3...)"
                  value={newTopic.order}
                  onChange={(e) => 
                    setNewTopic({ ...newTopic, order: parseInt(e.target.value) || 0 })
                  }
                  min="0"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => {
                  console.log("Closing Create Topic Modal...");
                  setShowCreateTopicModal(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTopic}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Topic
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Add New Question Modal */}
    {showAddModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Add New Question</h2>
            <button 
              onClick={() => {
                setShowAddModal(false);
                setNewQuestion({
                  text: "",
                  type: "mcq",
                  points: 1,
                  customPoints: "",
                  options: [
                    { text: "", image: null },
                    { text: "", image: null },
                    { text: "", image: null },
                    { text: "", image: null }
                  ],
                  correctOption: 0,
                  solution: "",
                  solutionImages: [],
                  difficultyLevel: "Easy",
                  questionYear: "",
                  hasImage: false,
                  images: []
                });
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6">
            {/* Hierarchical Location Info */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-800 mb-3">Question Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center mb-2">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-2 text-xs font-medium">1</span>
                    <label className="text-sm font-medium text-gray-700">Board</label>
                  </div>
                  <div className="text-sm bg-blue-50 text-blue-800 px-3 py-2 rounded border border-blue-100">
                    {boards.find(b => b.id === selectedBoard)?.name || "No board selected"}
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-2 text-xs font-medium">2</span>
                    <label className="text-sm font-medium text-gray-700">Standard</label>
                  </div>
                  <div className="text-sm bg-blue-50 text-blue-800 px-3 py-2 rounded border border-blue-100">
                    {standards.find(s => s.id === selectedStd)?.name || "No standard selected"}
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-2 text-xs font-medium">3</span>
                    <label className="text-sm font-medium text-gray-700">Subject</label>
                  </div>
                  <div className="text-sm bg-blue-50 text-blue-800 px-3 py-2 rounded border border-blue-100">
                    {subjects.find(s => s.id === selectedSubject)?.name || "No subject selected"}
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-2 text-xs font-medium">4</span>
                    <label className="text-sm font-medium text-gray-700">Chapter</label>
                  </div>
                  <div className="text-sm bg-blue-50 text-blue-800 px-3 py-2 rounded border border-blue-100">
                    {chapters.find(c => c.id === selectedChapter)?.name || "No chapter selected"}
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mr-2 text-xs font-medium">5</span>
                    <label className="text-sm font-medium text-gray-700">Topic</label>
                  </div>
                  <div className="text-sm bg-blue-50 text-blue-800 px-3 py-2 rounded border border-blue-100">
                    {topics.find(t => t.id === selectedTopic)?.name || "No topic selected"}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Question Meta Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Type
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={newQuestion.type}
                  onChange={(e) =>
                    setNewQuestion((prev) => ({
                      ...prev,
                      type: e.target.value,
                      options: e.target.value === "mcq"
                        ? [
                            { text: "", image: null },
                            { text: "", image: null },
                            { text: "", image: null },
                            { text: "", image: null }
                          ]
                        : [],
                    }))
                  }
                >
                  <option value="mcq">Multiple Choice (MCQ)</option>
                  <option value="descriptive">Descriptive</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty Level
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={newQuestion.difficultyLevel}
                  onChange={(e) =>
                    setNewQuestion((prev) => ({
                      ...prev,
                      difficultyLevel: e.target.value,
                    }))
                  }
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={newQuestion.points === "" ? "Custom" : newQuestion.points}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewQuestion((prev) => ({
                      ...prev,
                      points: value === "Custom" ? "" : parseInt(value),
                      customPoints: value === "Custom" ? prev.customPoints : "",
                    }));
                  }}
                >
                  {getCurrentPointCategories().map((p) => (
                    <option key={p} value={p}>
                      {p === "Custom" ? "Custom Points" : `${p} Point${p > 1 ? "s" : ""}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {newQuestion.points === "" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Points Value
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="Enter points value"
                    value={newQuestion.customPoints}
                    onChange={(e) =>
                      setNewQuestion((prev) => ({
                        ...prev,
                        customPoints: parseInt(e.target.value) || "",
                      }))
                    }
                    min="1"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year Asked (Optional)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  placeholder="YYYY (e.g., 2023)"
                  value={newQuestion.questionYear}
                  onChange={(e) =>
                    setNewQuestion((prev) => ({
                      ...prev,
                      questionYear: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {/* Question Content */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Text
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                rows={4}
                placeholder="Enter your question here..."
                value={newQuestion.text}
                onChange={(e) =>
                  setNewQuestion((prev) => ({
                    ...prev,
                    text: e.target.value,
                  }))
                }
              />
              
              {/* Question Images */}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Question Images
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hasImage"
                      checked={newQuestion.hasImage}
                      onChange={(e) =>
                        setNewQuestion((prev) => ({
                          ...prev,
                          hasImage: e.target.checked,
                          images: e.target.checked ? prev.images : [],
                        }))
                      }
                      className="mr-2"
                    />
                    <label htmlFor="hasImage" className="text-sm text-gray-600">
                      Include Images
                    </label>
                  </div>
                </div>
                
                {newQuestion.hasImage && (
                  <div className="mt-2">
                    <div className="flex items-center">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          setNewQuestion((prev) => ({
                            ...prev,
                            images: [...prev.images, ...files],
                          }));
                        }}
                      />
                    </div>
                    
                    {/* Preview uploaded images */}
                    {newQuestion.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {newQuestion.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Question Image ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-md border border-gray-300"
                            />
                            <button
                              onClick={() => {
                                setNewQuestion((prev) => ({
                                  ...prev,
                                  images: prev.images.filter((_, i) => i !== index),
                                }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Remove image"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* MCQ Options */}
            {newQuestion.type === "mcq" && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="font-medium text-gray-800 mb-3">Answer Options</h3>
                <div className="space-y-4">
                  {newQuestion.options.map((option, index) => (
                    <div key={index} className="p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id={`option-${index}`}
                            name="correct-answer"
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            checked={newQuestion.correctOption === index}
                            onChange={() =>
                              setNewQuestion((prev) => ({
                                ...prev,
                                correctOption: index,
                              }))
                            }
                          />
                          <label htmlFor={`option-${index}`} className="ml-2 text-sm font-medium text-gray-700">
                            Correct Answer
                          </label>
                        </div>
                        <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          Option {index + 1}
                        </span>
                      </div>
                      
                      <div className="flex flex-col md:flex-row gap-3">
                        {/* Option Text */}
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Option Text</label>
                          <input
                            type="text"
                            placeholder={`Text for option ${index + 1}`}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                            value={option.text || ""}
                            onChange={(e) =>
                              setNewQuestion((prev) => ({
                                ...prev,
                                options: prev.options.map((opt, idx) =>
                                  idx === index ? { ...opt, text: e.target.value } : opt
                                ),
                              }))
                            }
                          />
                        </div>
                        
                        {/* Option Image */}
                        <div className="md:w-1/3">
                          <label className="block text-xs text-gray-500 mb-1">Option Image (Optional)</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;

                                setNewQuestion((prev) => ({
                                  ...prev,
                                  options: prev.options.map((opt, idx) =>
                                    idx === index ? { ...opt, image: file } : opt
                                  ),
                                }));
                              }}
                            />
                            
                            {option.image && (
                              <div className="relative flex-shrink-0">
                                <img
                                  src={typeof option.image === "string" ? option.image : URL.createObjectURL(option.image)}
                                  alt={`Option ${index + 1} Preview`}
                                  className="w-10 h-10 object-cover rounded-md border border-gray-300"
                                />
                                <button
                                  onClick={() => {
                                    setNewQuestion((prev) => ({
                                      ...prev,
                                      options: prev.options.map((opt, idx) =>
                                        idx === index ? { ...opt, image: null } : opt
                                      ),
                                    }));
                                  }}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                  title="Remove image"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Solution/Explanation */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h3 className="font-medium text-gray-800 mb-3">Solution & Explanation</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Solution Text
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  rows={3}
                  placeholder="Enter the solution or explanation..."
                  value={newQuestion.solution}
                  onChange={(e) =>
                    setNewQuestion((prev) => ({
                      ...prev,
                      solution: e.target.value,
                    }))
                  }
                />
              </div>
              
              {/* Solution Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Solution Images (Optional)
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setNewQuestion((prev) => ({
                      ...prev,
                      solutionImages: [...prev.solutionImages, ...files],
                    }));
                  }}
                />
                
                {/* Preview solution images */}
                {newQuestion.solutionImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {newQuestion.solutionImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Solution Image ${index + 1}`}
                          className="w-24 h-24 object-cover rounded-md border border-gray-300"
                        />
                        <button
                          onClick={() => {
                            setNewQuestion((prev) => ({
                              ...prev,
                              solutionImages: prev.solutionImages.filter((_, i) => i !== index),
                            }));
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove image"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewQuestion({
                    text: "",
                    type: "mcq",
                    points: 1,
                    customPoints: "",
                    options: [
                      { text: "", image: null },
                      { text: "", image: null },
                      { text: "", image: null },
                      { text: "", image: null }
                    ],
                    correctOption: 0,
                    solution: "",
                    solutionImages: [],
                    difficultyLevel: "Easy",
                    questionYear: "",
                    hasImage: false,
                    images: []
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddQuestion}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Question
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Edit Question Modal */}
    {showEditModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Edit Question</h2>
            <button 
              onClick={() => {
                setShowEditModal(false);
                setEditingQuestion(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6">
            {/* Question Meta Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Type
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={editingQuestion?.type || "mcq"}
                  onChange={(e) =>
                    setEditingQuestion((prev) => ({
                      ...prev,
                      type: e.target.value,
                      options: e.target.value === "mcq"
                        ? prev.options?.length ? prev.options : [
                            { text: "", image: null },
                            { text: "", image: null },
                            { text: "", image: null },
                            { text: "", image: null }
                          ]
                        : [],
                    }))
                  }
                >
                  <option value="mcq">Multiple Choice (MCQ)</option>
                  <option value="descriptive">Descriptive</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty Level
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={editingQuestion?.difficultyLevel || "Easy"}
                  onChange={(e) =>
                    setEditingQuestion((prev) => ({
                      ...prev,
                      difficultyLevel: e.target.value,
                    }))
                  }
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Points
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  value={editingQuestion?.points > 4 ? "Custom" : editingQuestion?.points}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingQuestion((prev) => ({
                      ...prev,
                      points: value === "Custom" ? "" : parseInt(value),
                      customPoints: value === "Custom" ? prev.customPoints || "" : "",
                    }));
                  }}
                >
                  {pointCategories.map((p) => (
                    <option key={p} value={p}>
                      {p === "Custom" ? "Custom Points" : `${p} Point${p > 1 ? "s" : ""}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {editingQuestion?.points === "" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Points Value
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    placeholder="Enter points value"
                    value={editingQuestion?.customPoints || ""}
                    onChange={(e) =>
                      setEditingQuestion((prev) => ({
                        ...prev,
                        customPoints: parseInt(e.target.value) || "",
                      }))
                    }
                    min="1"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year Asked (Optional)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  placeholder="YYYY (e.g., 2023)"
                  value={editingQuestion?.questionYear || ""}
                  onChange={(e) =>
                    setEditingQuestion((prev) => ({
                      ...prev,
                      questionYear: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            
            {/* Question Content */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Text
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                rows={4}
                placeholder="Enter your question here..."
                value={editingQuestion?.text || ""}
                onChange={(e) =>
                  setEditingQuestion((prev) => ({
                    ...prev,
                    text: e.target.value,
                  }))
                }
              />
              
              {/* Question Images */}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Question Images
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hasImage"
                      checked={editingQuestion?.hasImage}
                      onChange={(e) =>
                        setEditingQuestion((prev) => ({
                          ...prev,
                          hasImage: e.target.checked,
                        }))
                      }
                      className="mr-2"
                    />
                    <label htmlFor="hasImage" className="text-sm text-gray-600">
                      Include Images
                    </label>
                  </div>
                </div>
                
                {editingQuestion?.hasImage && (
                  <>
                    {/* Existing Images */}
                    {editingQuestion?.existingImages?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Existing Images</p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {editingQuestion.existingImages.map((img, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={img}
                                alt={`Question Image ${index + 1}`}
                                className="w-24 h-24 object-cover rounded-md border border-gray-300"
                              />
                              <button
                                onClick={() => handleDeleteExistingImage(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove image"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* New Images Upload */}
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-2">Add New Images</p>
                      <div className="flex items-center">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            setEditingQuestion((prev) => ({
                              ...prev,
                              newImages: [...(prev.newImages || []), ...files],
                            }));
                          }}
                        />
                      </div>
                      
                      {/* Preview new uploaded images */}
                      {editingQuestion?.newImages?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {editingQuestion.newImages.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={URL.createObjectURL(image)}
                                alt={`New Image ${index + 1}`}
                                className="w-24 h-24 object-cover rounded-md border border-gray-300"
                              />
                              <button
                                onClick={() => {
                                  setEditingQuestion((prev) => ({
                                    ...prev,
                                    newImages: prev.newImages.filter((_, i) => i !== index),
                                  }));
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove image"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* MCQ Options */}
            {editingQuestion?.type === "mcq" && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="font-medium text-gray-800 mb-3">Answer Options</h3>
                <div className="space-y-4">
                  {editingQuestion.options?.map((option, index) => (
                    <div key={index} className="p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id={`edit-option-${index}`}
                            name="edit-correct-answer"
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            checked={editingQuestion.correctOption === index}
                            onChange={() =>
                              setEditingQuestion((prev) => ({
                                ...prev,
                                correctOption: index,
                              }))
                            }
                          />
                          <label htmlFor={`edit-option-${index}`} className="ml-2 text-sm font-medium text-gray-700">
                            Correct Answer
                          </label>
                        </div>
                        <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          Option {index + 1}
                        </span>
                      </div>
                      
                      <div className="flex flex-col md:flex-row gap-3">
                        {/* Option Text */}
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Option Text</label>
                          <input
                            type="text"
                            placeholder={`Text for option ${index + 1}`}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                            value={option?.text || ""}
                            onChange={(e) =>
                              setEditingQuestion((prev) => ({
                                ...prev,
                                options: prev.options?.map((opt, idx) =>
                                  idx === index ? { ...opt, text: e.target.value } : opt
                                ),
                              }))
                            }
                          />
                        </div>
                        
                        {/* Option Image */}
                        <div className="md:w-1/3">
                          <label className="block text-xs text-gray-500 mb-1">Option Image (Optional)</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;

                                setEditingQuestion((prev) => ({
                                  ...prev,
                                  options: prev.options?.map((opt, idx) =>
                                    idx === index ? { ...opt, image: file } : opt
                                  ),
                                }));
                              }}
                            />
                            
                            {option?.image && (
                              <div className="relative flex-shrink-0">
                                <img
                                  src={typeof option.image === "string" ? option.image : URL.createObjectURL(option.image)}
                                  alt={`Option ${index + 1} Preview`}
                                  className="w-10 h-10 object-cover rounded-md border border-gray-300"
                                />
                                <button
                                  onClick={() => {
                                    setEditingQuestion((prev) => ({
                                      ...prev,
                                      options: prev.options?.map((opt, idx) =>
                                        idx === index ? { ...opt, image: null } : opt
                                      ),
                                    }));
                                  }}
                                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                  title="Remove image"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Solution/Explanation */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h3 className="font-medium text-gray-800 mb-3">Solution & Explanation</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Solution Text
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  rows={3}
                  placeholder="Enter the solution or explanation..."
                  value={editingQuestion?.solution || ""}
                  onChange={(e) =>
                    setEditingQuestion((prev) => ({
                      ...prev,
                      solution: e.target.value,
                    }))
                  }
                />
              </div>
              
              {/* Solution Images */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Solution Images
                  </label>
                </div>
                
                {/* Existing Solution Images */}
                {editingQuestion?.existingSolutionImages?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Existing Solution Images</p>
                    <div className="flex flex-wrap gap-2">
                      {editingQuestion.existingSolutionImages.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={img}
                            alt={`Solution Image ${index + 1}`}
                            className="w-24 h-24 object-cover rounded-md border border-gray-300"
                          />
                          <button
                            onClick={() => {
                              setEditingQuestion((prev) => ({
                                ...prev,
                                existingSolutionImages: prev.existingSolutionImages.filter((_, i) => i !== index),
                              }));
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove image"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* New Solution Images */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Add New Solution Images</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setEditingQuestion((prev) => ({
                        ...prev,
                        newSolutionImages: [...(prev.newSolutionImages || []), ...files],
                      }));
                    }}
                  />
                  
                  {/* Preview new solution images */}
                  {editingQuestion?.newSolutionImages?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {editingQuestion.newSolutionImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`New Solution Image ${index + 1}`}
                            className="w-24 h-24 object-cover rounded-md border border-gray-300"
                          />
                          <button
                            onClick={() => {
                              setEditingQuestion((prev) => ({
                                ...prev,
                                newSolutionImages: prev.newSolutionImages.filter((_, i) => i !== index),
                              }));
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove image"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingQuestion(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleEditQuestion}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default QuestionUpload;